import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NewsletterSignup from '../components/NewsletterSignup';
import SiteFooter from '../components/SiteFooter';
import MarketingNav from '../components/MarketingNav';
import { COMPANY, CONTACT_PATH, LEGAL_PATHS, OFFERING_PATH } from '../legal/company';
import { useNotify } from '../context/NotifyContext';
import usePageMeta from '../hooks/usePageMeta';

const VOICE_LOGIN_MESSAGE = 'Login is required to use this feature.';

const LandingPage: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { notify } = useNotify();

  // No title: this is the page public/index.html's own title and description were
  // written for, and repeating them here would only risk the two drifting apart.
  usePageMeta({ path: '/' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      notify.error('Please describe your business');
      return;
    }
    if (!isAuthenticated) {
      navigate('/login', { state: { prompt, from: '/create-website' } });
    } else {
      navigate('/create-website', { state: { prompt } });
    }
  };

  // Voice recording is only wired up post-login (it needs an authenticated
  // upload to the transcription endpoint), so on this public landing page
  // the button is just a visual affordance — clicking it sends the visitor
  // to sign in first, preserving whatever they've already typed.
  const handleVoiceClick = () => {
    navigate('/login', { state: { prompt, from: '/create-website', message: VOICE_LOGIN_MESSAGE } });
  };

  const features = [
    {
      title: 'Speak it. We build it.',
      desc: 'Describe your business in English or Hindi — type, talk, or upload a note. OnTheWeb turns that into a full website.',
      icon: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z',
    },
    {
      title: 'Your photos, your look',
      desc: 'Add pictures of your shop, products, or work. The site is designed around the real business, not stock filler.',
      icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    },
    {
      title: 'Designed and written for you',
      desc: 'AI picks the layout, writes the copy, chooses a theme, and can create a logo — catalogue, gallery, hours, offers, and more.',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    },
    {
      title: 'Edit with AI, anytime',
      desc: 'Rewrite a section, add a new one, or restyle the whole site from a prompt or a voice note. Publish when it feels right.',
      icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    },
    {
      title: 'Enquiries land with you',
      desc: 'Visitors send messages from the contact form. You see them in Enquiries, collect newsletter signups, and watch visits in analytics.',
      icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    },
    {
      title: 'Beautiful on every phone',
      desc: 'Every site is built to look sharp on mobile first, then on tablet and desktop — ready to share the moment you publish.',
      icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
    },
  ];

  const steps = [
    { step: '01', title: 'Describe Your Business', desc: 'Tell us about your business in a few sentences — what you do, who you serve, and what makes you special.' },
    { step: '02', title: 'AI Creates Your Website', desc: 'Our AI analyzes your description and generates a complete website with design, content, and structure in seconds.' },
    { step: '03', title: 'Customize & Publish', desc: 'Review your website, make any changes you want, and publish it live with one click.' },
  ];

  const websiteFeatures = [
    'AI website from a prompt, voice, or photos',
    'Speak in English or Hindi',
    'Connect your own domain',
    'Get found on Google and across the web',
    'Control your page title, description, and SEO details',
    'As many sections as your business needs — catalogue, gallery, hours, offers',
    'Update your content, photos, and theme anytime, with AI',
    'Enquiries, newsletter signups, and visit analytics',
    'Growth playbooks and market updates to win more customers',
    'Looks stunning on every phone',
  ];

  const plans: {
    name: string;
    price: string;
    /** Pre-discount price, rendered struck through next to `price`. */
    originalPrice?: string;
    period: string;
    blurb: string;
    features: string[];
    cta: string;
    popular: boolean;
  }[] = [
    {
      name: 'Monthly',
      price: '₹499',
      originalPrice: '₹999',
      period: '/month + 18% GST',
      blurb: 'The complete website builder, billed monthly.',
      features: websiteFeatures,
      cta: 'Get started',
      popular: false,
    },
    {
      name: 'Yearly',
      price: '₹1,299',
      originalPrice: '₹9,990',
      period: '/year + 18% GST',
      blurb: 'The complete website builder, billed once a year.',
      features: websiteFeatures,
      cta: 'Get started',
      popular: true,
    },
    {
      name: 'Custom',
      price: 'Custom',
      period: 'quote',
      blurb: 'A website, ERP, CRM, and other software — shaped around how you work.',
      features: [
        'Website, ERP, CRM, and business software together',
        'Designed around your workflows and teams',
        'One partner from idea to go-live',
        'Priority conversation with our team',
      ],
      cta: 'Contact us',
      popular: false,
    },
  ];

  const faqs: { q: string; a: React.ReactNode }[] = [
    {
      q: 'Do I need any technical skills?',
      a: 'No. Login or signup with your phone, describe your business in English or Hindi (type or speak), and OnTheWeb generates the design, copy, and pages. You can then edit everything from the dashboard — no coding.',
    },
    {
      q: 'How do I create a website?',
      a: 'Tell us about the business in a prompt, optionally attach photos of your shop, products, or work, and start generation. The AI chooses sections (hero, products or services, gallery, hours, contact, and more), writes content, picks a theme, and can create a logo. It usually takes a few minutes. Preview the draft, then publish when you are happy.',
    },
    {
      q: 'What can go on my website?',
      a: 'Typical sections include navigation, hero, products or a categorised catalogue, services, business hours, offers, gallery (including categorised galleries), team, testimonials, FAQs, pricing, blog, video, clients, newsletter signup, social links, legal pages, and a contact form. You can add, remove, reorder, and change layouts after generation.',
    },
    {
      q: 'Can I edit the site after it is generated?',
      a: 'Yes. Change text, images, theme colours, and layouts anytime. Add a new section by choosing its type and describing what you want — the same generator used at creation fills it in. You can also use Edit with AI on an existing section to rewrite it from a prompt or voice note. Publish again to update the live site.',
    },
    {
      q: 'Can I use Hindi or voice?',
      a: 'Yes. Speak in English or Hindi; spoken Hindi is transcribed and translated so the website is generated in English. You can also type the prompt or upload a text file, and use voice the same way when adding or editing a section.',
    },
    {
      q: 'Can I use my own photos?',
      a: 'Yes. Add photos of your shop, products, or work when you create the site. The generator uses them so the design and gallery feel like your business.',
    },
    {
      q: 'Where does my website go live?',
      a: 'Each site gets its own live OnTheWeb address. Preview the draft, then publish and share the link. It looks great on phones.',
    },
    {
      q: 'How do visitors contact me?',
      a: 'The contact section includes a form. Submissions appear under Enquiries in your dashboard (with email and phone when the visitor provides them). If you add a newsletter block, signup emails are collected under Newsletter.',
    },
    {
      q: 'Can I see how the site is performing?',
      a: 'Yes. Analytics in the dashboard shows visits to your published site. Combined with Enquiries and Newsletter, you can see both traffic and the leads the site is collecting.',
    },
    {
      q: 'How do I contact OnTheWeb?',
      a: (
        <>
          Use the{' '}
          <Link to={CONTACT_PATH} className="text-nova-sky hover:text-nova-sky2 font-medium">
            Contact us
          </Link>{' '}
          page, email {COMPANY.supportEmail}, or call {COMPANY.phone}. For legal or privacy requests, write to{' '}
          {COMPANY.legalEmail}.
        </>
      ),
    },
    {
      q: 'What does it cost? What about refunds?',
      a: (
        <>
          Website plans are ₹499/month or ₹1,299/year, plus 18% GST, with all OnTheWeb website features included. Domain charges are separate. For websites plus ERP, CRM, or other software, ask for a Custom quote. Cancellations and refunds follow our{' '}
          <Link to={LEGAL_PATHS.refunds} className="text-nova-sky hover:text-nova-sky2 font-medium">
            Refund &amp; Cancellation Policy
          </Link>
          . For billing questions, write to {COMPANY.supportEmail}.
        </>
      ),
    },
    {
      q: 'Who owns the content?',
      a: 'You own the information, photos, and copy you provide, and you control the published website. OnTheWeb hosts it so visitors can view it. You can edit or delete content and sites from your account at any time.',
    },
  ];

  return (
    <div className="nova-page">
      <MarketingNav />

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6 lg:px-12 overflow-hidden">
        <div className="hero-glow top-0 left-1/2 -translate-x-1/2" />
        <div className="max-w-content mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="hero-badge">
              <span className="badge-dot" />
              Trusted by 10,000+ businesses worldwide
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-7xl font-extrabold text-nova-text mb-6 leading-[1.1]">
              Build your dream website with{' '}
              <span className="text-nova-sky">AI magic</span>
            </h1>
            <p className="text-xl text-nova-text2 mb-12 leading-relaxed max-w-2xl mx-auto">
              No coding. No design skills. Just tell us about your business,
              and watch as our AI creates a stunning, professional website in minutes.
            </p>

            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto mb-10">
              <div className="nova-card p-6 md:p-8 text-left shadow-nova-lg">
                <label htmlFor="business-prompt" className="nova-label text-base text-nova-text mb-3">
                  Describe your business in a few words
                </label>
                <button
                  type="button"
                  onClick={handleVoiceClick}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 mb-4 rounded-lg text-sm font-semibold transition-all bg-nova-sky/10 text-nova-sky border border-nova-sky/30 hover:bg-nova-sky/20"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                  Record your description
                </button>
                <textarea
                  id="business-prompt"
                  rows={5}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="nova-input resize-none mb-4"
                  placeholder="Example: I run a cozy Italian restaurant in downtown. We specialize in authentic homemade pasta and wood-fired pizzas..."
                />
                <button type="submit" className="btn-hero w-full justify-center text-base py-4">
                  Create My Website Free
                  <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="8" x2="13" y2="8" /><polyline points="9,4 13,8 9,12" />
                  </svg>
                </button>
                <p className="text-center text-sm text-nova-text3 mt-3">
                  No credit card required · Takes less than 2 minutes
                </p>
              </div>
            </form>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-nova-text2">
              {['Trusted by 10,000+ businesses', '98% satisfaction rate', '24/7 support'].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-nova-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {[
              { number: '10K+', label: 'Websites Created' },
              { number: '98%', label: 'Customer Satisfaction' },
              { number: '2 Min', label: 'Average Setup Time' },
              { number: '24/7', label: 'Support Available' },
            ].map((stat) => (
              <div key={stat.label} className="nova-card text-center py-5">
                <div className="font-display text-3xl font-extrabold text-nova-sky mb-1">{stat.number}</div>
                <div className="text-sm text-nova-text2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 lg:px-12 bg-nova-bg2">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <div className="section-tag">Features</div>
            <h2 className="section-title mx-auto">A complete website, from a conversation</h2>
            <p className="section-sub mx-auto mt-4">
              Speak, type, or share photos. OnTheWeb designs the pages, writes the copy, and puts enquiries in your hands.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="nova-card-hover group">
                <div className="w-12 h-12 rounded-xl bg-nova-sky/10 border border-nova-sky/20 flex items-center justify-center mb-5 group-hover:bg-nova-sky/20 transition-colors">
                  <svg className="w-6 h-6 text-nova-sky" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={f.icon} />
                  </svg>
                </div>
                <h3 className="font-display text-xl font-bold text-nova-text mb-3">{f.title}</h3>
                <p className="text-nova-text2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6 lg:px-12">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <div className="section-tag">How It Works</div>
            <h2 className="section-title mx-auto">Your website in 3 simple steps</h2>
            <p className="section-sub mx-auto mt-4">Building a professional website has never been easier.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.step} className="nova-card-hover relative">
                <div className="font-display text-5xl font-extrabold text-nova-sky/20 mb-4">{s.step}</div>
                <h3 className="font-display text-xl font-bold text-nova-text mb-3">{s.title}</h3>
                <p className="text-nova-text2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-12 bg-nova-bg3 border-y border-nova-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl font-extrabold text-nova-text mb-4">Ready to get started?</h2>
          <p className="text-xl text-nova-text2 mb-8">Join thousands of businesses who've already created their perfect website</p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="btn-hero">
            Create Your Website Now
            <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="8" x2="13" y2="8" /><polyline points="9,4 13,8 9,12" />
            </svg>
          </button>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 lg:px-12">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <div className="section-tag">Pricing</div>
            <h2 className="section-title mx-auto">Simple, transparent pricing</h2>
            <p className="section-sub mx-auto mt-4">
              Every website feature on Monthly and Yearly, at introductory pricing. Pay in Indian rupees.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto pt-5 items-stretch">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`nova-card relative flex flex-col h-full ${plan.popular ? 'border-nova-sky shadow-nova-lg z-10' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="bg-nova-sky text-nova-sky4 px-4 py-1 rounded-full text-xs font-bold">Most Popular</span>
                  </div>
                )}
                <h3 className="font-display text-xl font-bold text-nova-text mb-1">{plan.name}</h3>
                <p className="text-sm text-nova-text2 mb-4">{plan.blurb}</p>
                <div className="mb-6">
                  {plan.originalPrice && (
                    <span className="font-display text-xl sm:text-2xl font-bold text-nova-text3 line-through mr-2">
                      {plan.originalPrice}
                    </span>
                  )}
                  <span className="font-display text-3xl sm:text-4xl font-extrabold text-nova-text">{plan.price}</span>
                  {' '}
                  <span className="text-nova-text2">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-3 text-nova-text2 text-sm">
                      <svg className="w-5 h-5 text-nova-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feat}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() =>
                    plan.cta === 'Contact us'
                      ? navigate(CONTACT_PATH)
                      : window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                  className={`mt-auto w-full py-3 rounded-lg font-semibold transition-all ${plan.popular ? 'btn-primary justify-center' : 'btn-ghost justify-center'}`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-nova-text3 mt-8 max-w-xl mx-auto">
            Domain charges are separate. Refunds follow the{' '}
            <Link to={LEGAL_PATHS.refunds} className="text-nova-sky hover:text-nova-sky2">
              Refund &amp; Cancellation Policy
            </Link>
            .{' '}
            <Link to={OFFERING_PATH} className="text-nova-sky hover:text-nova-sky2">
              See what is included and what is not
            </Link>{' '}
            — also in Hindi.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 lg:px-12 bg-nova-bg2">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-tag">FAQ</div>
            <h2 className="section-title mx-auto">Frequently asked questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="nova-card group">
                <summary className="font-semibold text-nova-text cursor-pointer flex justify-between items-center list-none">
                  <span>{faq.q}</span>
                  <svg className="w-5 h-5 text-nova-text3 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="mt-4 text-nova-text2 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <NewsletterSignup />

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
